import { Receipt } from '@/types/expense';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { base64ToDataUrl } from '@/utils/image';
import { Calendar, Building2, FileText, Trash2, Maximize2 } from 'lucide-react';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { useState, useEffect } from 'react';
import { receiptTypeService, ReceiptTypeDto } from '@/services/receiptTypeService';
import { useDataMode } from '@/providers/DataModeProvider';
import { getReceiptTypeConfig } from '@/config/receipt-types';

interface ReceiptCardProps {
  receipt: Receipt;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  onViewReceipt?: (receipt: Receipt) => void;
  showDelete?: boolean;
}

export function ReceiptCard({ receipt, isSelected, onSelect, onDelete, onViewReceipt, showDelete = false }: ReceiptCardProps) {
  const backendUrl = useGetBackendUrl();
  const { isLiveMode } = useDataMode();
  const [receiptTypes, setReceiptTypes] = useState<ReceiptTypeDto[]>([]);

  useEffect(() => {
    const fetchReceiptTypes = async () => {
      try {
        const types = await receiptTypeService.getAll(isLiveMode);
        setReceiptTypes(types);
      } catch (error) {
        console.error('Failed to fetch receipt types:', error);
      }
    };

    fetchReceiptTypes();
  }, [isLiveMode]);
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(receipt.id);
    }
  };

  const handleCardClick = () => {
    onSelect(receipt.id);
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewReceipt) {
      onViewReceipt(receipt);
    }
  };
  
  return (
    <Card 
      onClick={handleCardClick}
      data-receipt-card
      className={`overflow-hidden transition-all hover:shadow-md relative cursor-pointer ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}
    >
      <CardContent className="p-0">
        {showDelete && onDelete && (
          <div className="absolute top-2 right-2 z-10">
            <Button
              variant="destructive"
              size="icon"
              className="h-7 w-7 shadow-md"
              onClick={handleDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        <div className="flex gap-3 p-3">
          {/* Thumbnail */}
          <div className="flex-shrink-0 relative group">
            <img
              src={base64ToDataUrl(receipt.thumbnailData || receipt.imageData)}
              alt="Receipt"
              className="w-20 h-28 object-cover rounded border cursor-pointer transition-opacity group-hover:opacity-75"
              onClick={handleImageClick}
            />
            <div 
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer bg-black/20 rounded"
              onClick={handleImageClick}
            >
              <div className="bg-white/90 rounded-full p-1.5 shadow-lg">
                <Maximize2 className="h-4 w-4 text-gray-700" />
              </div>
            </div>
          </div>

            {/* OCR Data */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Supplier */}
              {receipt.supplier && (
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{receipt.supplier}</p>
                  </div>
                </div>
              )}

              {/* Amount */}
              {receipt.amount !== undefined && receipt.amount !== null && (
                <div className="text-lg font-semibold text-primary">
                  £{receipt.amount.toFixed(2)}
                </div>
              )}

              {/* Transaction Date */}
              {receipt.transactionDate && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{format(new Date(receipt.transactionDate), 'dd MMM yyyy')}</span>
                </div>
              )}

              {/* Receipt Type */}
              {receipt.receiptType && (() => {
                const typeConfig = receiptTypes.find(t => t.id === receipt.receiptType) || getReceiptTypeConfig(receipt.receiptType);
                const typeName = typeConfig?.name === 'OfficeSupplies' ? 'Office Supplies' : (typeConfig?.name || 'Unknown');
                return (
                  <div className="flex items-start gap-2">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      {typeName}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>
  );
}
